import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useSupplierTierCounts } from '../../hooks/useSupplierTierCounts';
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  Factory,
  Eye,
  Newspaper,
  LogOut,
  X,
  User,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
} from 'lucide-react';
import { spacing } from '../../config/spacing';
import { typography } from '../../config/typography';
import { themeClasses } from '../../config/themeClasses';
import { Button } from '../UI/button';
import { cn } from '../../lib/utils';

const MONITORING_NAV = [
  { nameKey: 'dashboardTitle', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'companiesTitle', href: '/companies', icon: Building2 },
  { nameKey: 'myTenantCompanyTitle', href: '/my-tenant-company', icon: Factory },
  { nameKey: 'watchlistTitle', href: '/watchlist', icon: Eye, badgeKey: 'watchlistCount' },
];

const INSIGHTS_NAV = [{ nameKey: 'newsTitle', href: '/news', icon: Newspaper }];

const TIER_NAV = [
  { tier: '1', nameKey: 'sidebarTier1' },
  { tier: '2', nameKey: 'sidebarTier2' },
  { tier: '3', nameKey: 'sidebarTier3' },
];

const Sidebar = ({ isOpen, onClose, user, onLogout, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { t } = useLocale();
  const { watchlistStats, watchlist } = useAppData();
  const { tierCounts } = useSupplierTierCounts();
  const watchlistCount = watchlistStats?.totalCompanies ?? watchlist?.length ?? 0;
  const [suppliersExpanded, setSuppliersExpanded] = useState(false);

  const isMyCompaniesPath = location.pathname === '/my-companies';
  const tierParam = new URLSearchParams(location.search).get('tier');

  useEffect(() => {
    if (isMyCompaniesPath) setSuppliersExpanded(true);
  }, [isMyCompaniesPath]);

  const isActive = (href) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);

  const isTierActive = (tier) =>
    isMyCompaniesPath && String(tierParam || '') === String(tier);

  const sidebarWidth = collapsed
    ? spacing.layout.sidebar.collapsed
    : spacing.layout.sidebar.expanded;
  const sectionStyle = typography.textStyles.sidebarSectionHeader;
  const navStyle = typography.textStyles.sidebarNav;

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    const badge =
      item.badgeKey === 'watchlistCount' && watchlistCount > 0
        ? String(watchlistCount)
        : null;
    return (
      <Link
        key={item.nameKey}
        to={item.href}
        onClick={onClose}
        className={cn(
          'relative flex items-center rounded-sm transition-colors duration-200 border-l-[3px]',
          collapsed ? 'justify-center px-3 py-2.5' : 'px-3 py-2.5 gap-3',
          active ? themeClasses.nav.activeRow : themeClasses.nav.inactiveRow
        )}
        title={collapsed ? t(item.nameKey) : ''}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Icon className="h-5 w-5 shrink-0" aria-hidden />
          {!collapsed && (
            <span
              style={{
                fontSize: navStyle.fontSize,
                fontWeight: active ? '500' : navStyle.fontWeight,
              }}
            >
              {t(item.nameKey)}
            </span>
          )}
        </div>
        {!collapsed && badge && (
          <span className="shrink-0 rounded-full bg-primary-500 px-2 py-0.5 text-xs font-medium text-primary-900">
            {badge}
          </span>
        )}
        {collapsed && badge && (
          <span className="absolute right-0 top-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-500 px-1 text-xs font-medium text-primary-900">
            {badge}
          </span>
        )}
      </Link>
    );
  };

  const renderMySuppliersGroup = () => {
    const Icon = Briefcase;
    const parentActive = isMyCompaniesPath && !tierParam;

    if (collapsed) {
      return (
        <Link
          to="/my-companies"
          onClick={onClose}
          className={cn(
            'relative flex items-center justify-center rounded-sm border-l-[3px] px-3 py-2.5 transition-colors duration-200',
            isMyCompaniesPath ? themeClasses.nav.activeRow : themeClasses.nav.inactiveRow
          )}
          title={t('myCompaniesTitle')}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </Link>
      );
    }

    return (
      <div className="space-y-0.5">
        <div
          className={cn(
            'relative flex items-center rounded-sm border-l-[3px] transition-colors duration-200',
            parentActive ? themeClasses.nav.activeRow : themeClasses.nav.inactiveRow
          )}
        >
          <Link
            to="/my-companies"
            onClick={onClose}
            className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5"
          >
            <Icon className="h-5 w-5 shrink-0" aria-hidden />
            <span
              style={{
                fontSize: navStyle.fontSize,
                fontWeight: parentActive ? '500' : navStyle.fontWeight,
              }}
            >
              {t('myCompaniesTitle')}
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setSuppliersExpanded((v) => !v)}
            className="mr-2 shrink-0 rounded-sm p-1 text-text-secondary hover:bg-canvas-soft-2 hover:text-text-primary"
            aria-expanded={suppliersExpanded}
            aria-label={t('sidebarToggleSuppliersTiers')}
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', suppliersExpanded && 'rotate-180')}
              aria-hidden
            />
          </button>
        </div>
        {suppliersExpanded &&
          TIER_NAV.map((tierItem) => {
            const tier = tierItem.tier;
            const active = isTierActive(tier);
            const count = tierCounts[tier] ?? 0;
            return (
              <Link
                key={tier}
                to={`/my-companies?tier=${tier}`}
                onClick={onClose}
                className={cn(
                  'relative ml-6 flex items-center rounded-sm border-l-[3px] px-3 py-2 text-sm transition-colors duration-200',
                  active ? themeClasses.nav.activeRow : themeClasses.nav.inactiveRow
                )}
              >
                <span className="flex-1 truncate">{t(tierItem.nameKey)}</span>
                <span className="shrink-0 rounded-full bg-canvas-soft px-2 py-0.5 text-xs text-text-secondary">
                  {count}
                </span>
              </Link>
            );
          })}
      </div>
    );
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border-light bg-background-secondary shadow-elevation-1 transition-all duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        style={{ width: sidebarWidth }}
      >
        <div
          className={cn(
            'flex items-center border-b border-border-light',
            collapsed ? 'justify-center py-5 px-2' : 'gap-3 px-5 py-5'
          )}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-primary-500 text-primary-900">
            <Building2 className="h-5 w-5" aria-hidden />
          </div>
          {!collapsed && (
            <>
              <span
                className="truncate font-semibold text-text-primary"
                style={{ fontSize: typography.textStyles.sidebarLogo.fontSize }}
              >
                {t('appName')}
              </span>
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onToggleCollapse}
                className="hidden lg:flex"
                aria-label={t('sidebarCollapseSidebar')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onClose}
                className="lg:hidden"
                aria-label={t('sidebarCloseSidebar')}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
        {collapsed && (
          <Button
            variant="outline"
            size="icon-sm"
            onClick={onToggleCollapse}
            className="absolute -right-3 top-5 z-10 hidden rounded-full lg:flex"
            aria-label={t('sidebarExpandSidebar')}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        )}

        <nav className={cn('flex-1 overflow-y-auto py-4', collapsed ? 'px-2' : 'px-3')}>
          {!collapsed && (
            <p
              className="mb-2 px-3 font-mono uppercase tracking-wider text-text-tertiary"
              style={{ fontSize: sectionStyle.fontSize }}
            >
              {t('sidebarMonitoring')}
            </p>
          )}
          <div className="space-y-0.5">
            {MONITORING_NAV.slice(0, 2).map((item) => (
              <div key={item.nameKey}>{renderNavItem(item)}</div>
            ))}
            <div className="relative">{renderMySuppliersGroup()}</div>
            {MONITORING_NAV.slice(2).map((item) => (
              <div key={item.nameKey}>{renderNavItem(item)}</div>
            ))}
          </div>

          {!collapsed && (
            <p
              className="mb-2 mt-5 px-3 font-mono uppercase tracking-wider text-text-tertiary"
              style={{ fontSize: sectionStyle.fontSize }}
            >
              {t('sidebarInsights')}
            </p>
          )}
          <div className="space-y-0.5">
            {INSIGHTS_NAV.map((item) => (
              <div key={item.nameKey}>{renderNavItem(item)}</div>
            ))}
          </div>
        </nav>

        <div className="border-t border-border-light py-3">
          <div
            className={cn(
              'mx-3 flex items-center gap-3 rounded-md bg-canvas-soft px-3 py-3',
              collapsed && 'justify-center'
            )}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-primary-900">
              <User className="h-5 w-5" aria-hidden />
            </div>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text-primary">
                    {user?.firstname} {user?.lastname}
                  </p>
                  <p className="truncate text-xs text-text-tertiary">
                    {user?.role || 'Admin'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onLogout}
                  className="shrink-0 rounded-sm p-1.5 text-text-secondary transition-colors hover:bg-canvas-soft-2 hover:text-text-primary"
                  aria-label={t('sidebarSignOut')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
